import CheckmarkIcon from "@assets/disco/icons/color-icons/checkmark-circle.svg"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import CircularProgressWithLabel from "@components/circular-progress-with-label/CircularProgressWithLabel"
import { useTheme } from "@material-ui/core"
import classNames from "classnames"

interface ProgressIconProps {
  // a number between 0 and 1
  completion: number
  width?: number
  height?: number
  className?: string
  testid?: string
  variant?: "wedge" | "percentage"
  noCheckmarkIconAtCompletion?: boolean
}

function ProgressIcon({
  completion,
  width = 32,
  height,
  className,
  testid,
  variant = "wedge",
  noCheckmarkIconAtCompletion = false,
}: ProgressIconProps) {
  const theme = useTheme()
  const notStarted = isNaN(completion) || completion === 0
  const completed = completion >= 1
  const circumference = 2 * Math.PI * 5
  const wedge = completion * circumference
  const classes = useStyles({ width, height })
  const completionStatus = completed ? "complete" : "incomplete"

  if (variant === "percentage") {
    return (
      <div
        className={classNames(classes.percentageIcon, className)}
        data-testid={`${testid}.completion-icon.${completionStatus}`}
      >
        <CircularProgressWithLabel
          value={notStarted ? 0 : completion * 100}
          size={width}
          thickness={3}
          icon={
            completed &&
            !noCheckmarkIconAtCompletion && (
              <CheckmarkIcon className={classes.completeIcon} />
            )
          }
          className={classes.progress}
          showBuffer
          bufferValue={100}
          fontColor={theme.palette.groovy.neutral[500]}
          fontSize={10}
        />
      </div>
    )
  }

  return (
    <div
      className={classNames(classes.iconContainer, className)}
      data-testid={`${testid}.completion-icon.${completionStatus}`}
    >
      <svg viewBox={"0 0 24 24"}>
        <circle
          cx={"12"}
          cy={"12"}
          r={"11"}
          stroke={notStarted ? "#D5D9DE" : "#6FCF97"}
          strokeWidth={"2"}
        />
        <circle r={"10"} cx={"12"} cy={"12"} fill={"white"} />

        {renderInterior()}
      </svg>
    </div>
  )

  function renderInterior() {
    if (completed) {
      return (
        <path
          fillRule={"evenodd"}
          clipRule={"evenodd"}
          d={
            "M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21ZM16.7682 9.64018C17.1218 9.21591 17.0645 8.58534 16.6402 8.23178C16.2159 7.87821 15.5853 7.93554 15.2318 8.35982L11.6338 12.6774C11.2871 13.0934 11.0922 13.3238 10.9366 13.4653L10.9306 13.4707L10.9242 13.4659C10.7564 13.339 10.5415 13.1272 10.1585 12.7443L8.70711 11.2929C8.31658 10.9024 7.68342 10.9024 7.29289 11.2929C6.90237 11.6834 6.90237 12.3166 7.29289 12.7071L8.74428 14.1585L8.78511 14.1993L8.78512 14.1993C9.11161 14.526 9.4257 14.8402 9.71794 15.0611C10.0453 15.3087 10.474 15.5415 11.0234 15.5165C11.5728 15.4916 11.9787 15.221 12.2823 14.9448C12.5534 14.6983 12.8377 14.3569 13.1333 14.0021L13.1333 14.0021L13.1703 13.9577L16.7682 9.64018Z"
          }
          fill={"#6FCF97"}
        />
      )
    }

    return (
      <>
        <circle
          r={"5"}
          cx={"12"}
          cy={"12"}
          fill={"transparent"}
          stroke={notStarted ? undefined : "#6FCF97"}
          strokeWidth={"10"}
          strokeDasharray={`${wedge} ${circumference}`}
          transform={"rotate(-90) translate(-24)"}
        />
        <circle
          r={"9.5"}
          cx={"12"}
          cy={"12"}
          fill={"transparent"}
          strokeWidth={"1"}
          stroke={"white"}
        />
      </>
    )
  }
}

type StyleProps = {
  height?: number
  width?: number
}

const useStyles = makeUseStyles((theme) => ({
  iconContainer: (props: StyleProps) => ({
    height: props.height ? `${props.height}px` : "40px",
    width: props.width ? `${props.width}px` : "40px",
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(0, 1),

    "& svg": {
      width: "100%",
      height: "100%",
    },
  }),
  percentageIcon: {
    display: "flex",
  },
  progress: {
    color: theme.palette.groovy.green[500],
  },
  completeIcon: ({ width }: StyleProps) => ({
    position: "absolute",
    width: width ? `${width}px` : "32px",
  }),
}))

export default ProgressIcon
